import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * An useState that reads/write on local storage lazyly
 *
 * Reads from local storage on mount and writes on local storage on unmount.
 * This prevents unnecessary reads/write to local storage.
 *
 * @param {string} key - key used to write on local storage
 * @param {T} initialValue - value used to initialize state when local storage is empty
 * @returns {[T, Dispatch<SetStateAction<T>>]} - state and its setter
 * @example
 * const [name, setName] = useLazyLocalStorage("name");
 */
export const useLazyLocalStorage = <T>(
  key: string,
  initialValue?: T,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>] => {
  const [v, setV] = useState<T | undefined>(() => {
    const deserialize = (value: string) => {
      try {
        return JSON.parse(value) as T;
      } catch (err) {
        console.error("Error parsing JSON: ", err);
        return initialValue;
      }
    };
    const readFromLocalStorage = (): T | undefined => {
      try {
        const rawV = window.localStorage.getItem(key);
        return rawV ? deserialize(rawV) : initialValue;
      } catch (err) {
        console.error("Error retrieving value from local storage: ", err);
        return undefined;
      }
    };
    return readFromLocalStorage();
  });
  const latestV = useRef<T | undefined>(v);
  latestV.current = v; //sync v with ref

  useEffect(() => {
    const serialize = (value: T): string => {
      try {
        return JSON.stringify(value);
      } catch (err) {
        console.error("Error serializing value: ", err);
        return "";
      }
    };

    const writeOnLocalStorage = (value: T) => {
      try {
        const serializedV = serialize(value);
        window.localStorage.setItem(key, serializedV);
      } catch (err) {
        console.error("Error writing on local storage: ", err);
      }
    };

    const clearLocalStorage = () => {
      try {
        window.localStorage.removeItem(key);
      } catch (err) {
        console.error("Error cleaning local storage: ", err);
      }
    };

    return () => {
      // write/clear value on destroy
      if (latestV.current) {
        writeOnLocalStorage(latestV.current);
      } else {
        clearLocalStorage();
      }
    };
  }, [key]);

  return [v, setV];
}